import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { questionService, assessmentService } from '../services';
import QuestionCard from './QuestionCard';
import AiAssistant from './AiAssistant';
import { Button } from './ds';
import { useIsMobile } from '../hooks/useMediaQuery';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// Returns which questions are visible given the current answers to status queries.
// A question is hidden when its parent status query was answered 'no'.
function getVisibleQuestions(questions, answers) {
  return questions.filter(q => {
    if (!q.dependsOn) return true;
    const parentQ = questions.find(p => p.dinNr === q.dependsOn);
    if (!parentQ) return true;
    const parentAnswer = answers[parentQ.id]?.answer;
    return parentAnswer === 'yes';
  });
}

const parseDinNr = (dinNr) => {
  if (!dinNr) return [9999, 9999];
  const [main, sub] = dinNr.split('-');
  return [parseInt(main, 10), sub !== undefined ? parseInt(sub, 10) : -1];
};

// Standard-Datenzugriff über die authentifizierten Services. Der anonyme Flow
// (AUD-53) injiziert stattdessen eine `api`-Prop mit derselben Form, die gegen
// die öffentlichen /api/public-Endpunkte spricht — die Fragebogen-UI bleibt identisch.
const buildDefaultApi = (assessmentId, templateId) => ({
  getQuestions: () => questionService.getAllQuestions(templateId).then(r => r.data),
  getAnswers: () => questionService.getAnswersByAssessment(assessmentId).then(r => r.data),
  saveAnswer: ({ questionId, answer }) => questionService.saveAnswer({ assessmentId, questionId, answer }),
  complete: () => assessmentService.updateStatus(assessmentId, 'completed'),
});

const Questionnaire = ({ assessmentId, templateId, onComplete, api, enableAi = true }) => {
  const [questions, setQuestions]   = useState([]);
  const [answers, setAnswers]       = useState({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [aiOpen, setAiOpen]         = useState(false);
  const isMobile = useIsMobile();

  const svc = useMemo(
    () => api || buildDefaultApi(assessmentId, templateId),
    [api, assessmentId, templateId]
  );

  const loadQuestions = useCallback(async () => {
    try {
      const data = await svc.getQuestions();
      const sorted = [...data].sort((a, b) => {
        const [aM, aS] = parseDinNr(a.dinNr);
        const [bM, bS] = parseDinNr(b.dinNr);
        return aM !== bM ? aM - bM : aS - bS;
      });
      setQuestions(sorted);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [svc]);

  const loadAnswers = useCallback(async () => {
    try {
      const data = await svc.getAnswers();
      const map = {};
      data.forEach(a => { map[a.questionId] = a; });
      setAnswers(map);
    } catch (err) {
      console.error(err);
    }
  }, [svc]);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);
  useEffect(() => { loadAnswers(); }, [loadAnswers]);

  const handleAnswerChange = async (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: { ...prev[questionId], answer } }));
    try {
      setSaving(true);
      await svc.saveAnswer({ questionId, answer });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    try {
      setSaving(true);
      await svc.complete();
      onComplete?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Derive visible questions based on status-query answers
  const visibleQuestions = useMemo(
    () => getVisibleQuestions(questions, answers),
    [questions, answers]
  );

  // Clamp currentIndex when visible list shrinks
  const safeIndex = Math.min(currentIndex, Math.max(0, visibleQuestions.length - 1));

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px 0', color: '#8A93A3', fontSize: 14, fontFamily: FONT }}>Fragen werden geladen…</div>;
  }
  if (visibleQuestions.length === 0) {
    return <div style={{ textAlign: 'center', padding: '60px 0', color: '#8A93A3', fontSize: 14, fontFamily: FONT }}>Keine Fragen verfügbar.</div>;
  }

  const currentQuestion = visibleQuestions[safeIndex];
  const progress        = Math.round(((safeIndex + 1) / visibleQuestions.length) * 100);

  // Eine Frage gilt als beantwortet, wenn eine nicht-leere Antwort vorliegt.
  const isAnswered      = (q) => { const a = answers[q.id]?.answer; return a != null && a !== ''; };
  const answeredCount   = visibleQuestions.filter(isAnswered).length;
  const allAnswered     = answeredCount === visibleQuestions.length;
  const openCount       = visibleQuestions.length - answeredCount;
  const isLastQuestion  = safeIndex === visibleQuestions.length - 1;

  const categories = Array.from(new Set(visibleQuestions.map(q => q.category)));

  return (
    <>
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 230px', gap: 20, alignItems: 'start', fontFamily: FONT }}>

      {/* ── Left: question + navigation ── */}
      <div>
        {/* Progress bar */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '14px 20px',
          border: '1px solid #D7DCE4', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 16,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ height: 6, background: '#EBEEF3', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${progress}%`,
                background: '#1E4FBE',
                borderRadius: 999, transition: 'width 260ms cubic-bezier(0.16, 1, 0.3, 1)',
              }} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#667085', whiteSpace: 'nowrap', flexShrink: 0, display: 'flex', gap: 12, alignItems: 'center' }}>
            <span>
              <strong style={{ color: '#101623' }}>{safeIndex + 1}</strong>
              {' / '}{visibleQuestions.length}
              {' · '}
              {answeredCount} beantwortet
            </span>
            {saving && <span style={{ color: '#1E4FBE' }}>Speichert…</span>}
          </div>
        </div>

        {/* Question card */}
        <QuestionCard
          question={currentQuestion}
          answer={answers[currentQuestion.id]}
          onAnswerChange={answer => handleAnswerChange(currentQuestion.id, answer)}
          onOpenAi={enableAi ? () => setAiOpen(true) : undefined}
        />

        {/* Navigation */}
        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row',
          justifyContent: 'space-between', alignItems: 'center', gap: isMobile ? 10 : 0,
          marginTop: 14,
        }}>
          <Button
            variant="secondary"
            fullWidth={isMobile}
            onClick={() => setCurrentIndex(Math.max(0, safeIndex - 1))}
            disabled={safeIndex === 0}
            leadingIcon={<ArrowLeft width={14} height={14} />}
          >
            Zurück
          </Button>

          {!isLastQuestion ? (
            <Button
              fullWidth={isMobile}
              onClick={() => setCurrentIndex(safeIndex + 1)}
              trailingIcon={<ArrowRight width={14} height={14} />}
            >
              Weiter
            </Button>
          ) : (
            <Button
              fullWidth={isMobile}
              onClick={handleComplete}
              disabled={saving || !allAnswered}
              leadingIcon={<Check width={14} height={14} />}
              style={{ background: '#17864B', borderColor: '#17864B' }}
            >
              Assessment abschließen
            </Button>
          )}
        </div>

        {/* Hinweis auf der Abschluss-Seite, wenn noch Fragen offen sind — erklärt den
            deaktivierten Abschließen-Button. */}
        {isLastQuestion && !allAnswered && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 8,
            background: '#FDF6EA', border: '1px solid #FBEBD3',
            fontSize: 12.5, color: '#8A4B0A',
          }}>
            Noch {openCount} {openCount === 1 ? 'Frage ist' : 'Fragen sind'} unbeantwortet. Bitte
            beantworten Sie alle Fragen, um das Assessment abzuschließen.
          </div>
        )}
      </div>

      {/* ── Right: category sidebar (desktop/iPad only — mobile relies on the top progress bar) ── */}
      {!isMobile && (
        <div style={{
          background: '#fff', borderRadius: 12, padding: '18px 16px',
          border: '1px solid #D7DCE4', position: 'sticky', top: 24,
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: '#8A93A3', marginBottom: 10,
          }}>Themenbereiche</div>

          {categories.map(cat => {
            const catQs      = visibleQuestions.filter(q => q.category === cat);
            const catAnswered = catQs.filter(q => answers[q.id]).length;
            const catPct     = Math.round((catAnswered / catQs.length) * 100);
            const isActive   = currentQuestion.category === cat;

            return (
              <div
                key={cat}
                onClick={() => {
                  const firstIdx = visibleQuestions.findIndex(q => q.category === cat);
                  if (firstIdx !== -1) setCurrentIndex(firstIdx);
                }}
                style={{
                  marginBottom: 8, padding: '9px 10px', borderRadius: 8,
                  background: isActive ? '#F2F6FE' : 'transparent',
                  border: `1px solid ${isActive ? '#CBD9F9' : 'transparent'}`,
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  fontSize: 12, fontWeight: isActive ? 600 : 500,
                  color: isActive ? '#1B4098' : '#475569', marginBottom: 4,
                }}>{cat}</div>
                <div style={{ height: 3, background: '#EBEEF3', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${catPct}%`,
                    background: catPct === 100 ? '#17864B' : '#1E4FBE',
                    borderRadius: 999, transition: 'width 260ms',
                  }} />
                </div>
                <div style={{ fontSize: 10, color: '#8A93A3', marginTop: 3 }}>
                  {catAnswered}/{catQs.length}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    {enableAi && (
      <AiAssistant open={aiOpen} onClose={() => setAiOpen(false)} currentQuestion={currentQuestion} assessmentId={assessmentId} />
    )}
    </>
  );
};

export default Questionnaire;
